import { Component, ElementRef, EventEmitter, HostListener, inject, Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of } from 'rxjs';
import { GenericFormUICService } from 'src/app/Common/UICServices/FormUIC/form-uic.service';
import { GenericFormUICDTO } from 'src/app/Common/UICServices/FormUIC/form-uic.interface';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { ItemDetailDatalistService } from 'src/app/ERP/Products/service-master/service-detail/Item-detail-datalist.service';
import { ItemDetailDatalistCreateBulkItem } from 'src/app/ERP/Products/service-master/service-detail/Item-detail-datalist.interface';
import { UicFormComponent } from '../uic-form/uic-form.component';

@Component({
  selector: 'app-item-detail-data',
  templateUrl: './item-detail-data.component.html',
  styleUrls: ['./item-detail-data.component.css']
})
export class ItemDetailDataComponent implements OnInit, OnChanges {

  @Input() itemId = 0;
  @Input() uicMasterType = '';
  @Input() heading = '';
  @Input() addButtonLabel = '';
  @Input() validate?: (rows: ItemDetailDatalistCreateBulkItem[]) => string | null;
  @Output() ready = new EventEmitter<ItemDetailDataComponent>();

  @ViewChild('rowsForm') rowsForm?: UicFormComponent;

  uicForm: GenericFormUICDTO[] = [];
  columns: GenericFormUICDTO[] = [];
  valueColumns: GenericFormUICDTO[] = [];
  rowsFormGroup = new FormGroup({});
  savedRows: any[] = [];
  dType = '';
  hasConfig = false;
  showRows = false;

  private uicFormService = inject(GenericFormUICService);
  private iddService = inject(ItemDetailDatalistService);
  private toastr = inject(ToastrService);
  private User = inject(UserLoginService).User();
  private hostRef = inject(ElementRef);
  private renderer = inject(Renderer2);

  ngOnInit(): void {
    this.loadConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemId'] && !changes['itemId'].firstChange && this.hasConfig) {
      this.loadRows();
    }
  }

  @HostListener('click')
  onHostClick(): void {
    this.decorate();
  }

  get title(): string {
    return this.heading || 'Details';
  }

  get buttonLabel(): string {
    return this.addButtonLabel || 'Add ' + this.title;
  }

  addRow(): void {
    if (!this.showRows) {
      this.rowsFormGroup = new FormGroup({});
      this.showRows = true;
      this.decorate();
      return;
    }
    this.rowsForm?.addForm();
    this.decorate();
  }

  clearRows(): void {
    this.showRows = false;
    this.rowsFormGroup = new FormGroup({});
  }

  getRows(): any[] {
    if (!this.showRows) {
      return [];
    }
    const instances = this.rowsForm?.formInstances ?? [];
    const raw = instances.map(i => i.formGroup.getRawValue());
    return raw.filter(r => this.hasAnyValue(r));
  }

  validateRows(): string | null {
    if (!this.hasConfig || !this.validate) {
      return null;
    }
    return this.validate(this.buildPayload(this.itemId));
  }

  save(itemId: number = this.itemId): Observable<any> {
    if (!this.hasConfig || !itemId) {
      return of(null);
    }
    return forkJoin(this.persistCalls(this.buildPayload(itemId)));
  }

  reload(): void {
    this.loadRows();
  }

  private hasAnyValue(row: any): boolean {
    return this.valueColumns.some(c => {
      const value = row ? row[c.dbFeildName] : null;
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
  }

  private buildPayload(itemId: number): ItemDetailDatalistCreateBulkItem[] {
    return this.getRows().map((r, i) => ({
      ...r,
      datalistId: Number(r.datalistId ?? 0),
      itemId: itemId,
      dType: this.dType || r.dType || '',
      displaySeq: Number(r.displaySeq ?? i + 1)
    }));
  }

  private loadConfig(): void {
    if (!this.uicMasterType) {
      return;
    }

    this.uicFormService.GenericFormUIC_ReadByTenantId({
      masterType: this.uicMasterType,
      tenantId: this.User.companyId
    }).subscribe({
      next: res => {
        const items = res.items ?? [];
        if (!items.length) {
          this.hasConfig = false;
          return;
        }
        this.uicForm = items;
        this.columns = items
          .filter(f => f.isUIVisible === 1)
          .sort((a, b) => (a.displaySeq ?? 0) - (b.displaySeq ?? 0));
        this.valueColumns = this.columns.filter(f => !(f.wrapperClass ?? '').includes('d-none'));
        this.dType = (this.fieldByName('dtype')?.defaultValue ?? '').trim();
        if (!this.heading) {
          this.heading = this.fieldByName('pageheader')?.uiLabel ?? '';
        }
        this.hasConfig = true;
        this.ready.emit(this);
        if (this.itemId) {
          this.loadRows();
        }
      },
      error: () => {
        this.hasConfig = false;
      }
    });
  }

  private fieldByName(name: string): GenericFormUICDTO | undefined {
    return this.uicForm.find(f => (f.dbFeildName ?? '').toLowerCase().trim() === name);
  }

  private loadRows(): void {
    if (!this.itemId) {
      return;
    }

    this.iddService.ItemDetail_readByItemId({
      itemId: this.itemId,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    }).subscribe({
      next: res => {
        const all = res.items ?? [];
        const wanted = this.dType.trim().toUpperCase();
        this.savedRows = wanted
          ? all.filter(r => {
              const rowType = (r.dType ?? '').trim().toUpperCase();
              return rowType === wanted || rowType === '';
            })
          : all;

        if (!this.savedRows.length) {
          this.clearRows();
          return;
        }

        this.rowsFormGroup = new FormGroup({});
        this.showRows = true;
        this.patchIntoForm(0);
      },
      error: () => this.toastr.error('Unable to load ' + this.title)
    });
  }

  private patchIntoForm(attempt: number): void {
    setTimeout(() => {
      const form = this.rowsForm;

      if (!form?.isFormReady) {
        if (attempt < 30) {
          this.patchIntoForm(attempt + 1);
        }
        return;
      }

      while (form.formInstances.length < this.savedRows.length) {
        form.addForm();
      }

      this.savedRows.forEach((row, index) => {
        const instance = form.formInstances[index];
        if (!instance) {
          return;
        }
        this.seedSelectOptions(instance.formUIC, row);
        instance.formGroup.patchValue(this.rowToPatch(instance.formUIC, row));
      });

      this.decorate();
    }, attempt === 0 ? 0 : 100);
  }

  private rowToPatch(formUIC: GenericFormUICDTO[], row: any): any {
    const patch: any = {};
    formUIC.forEach(f => {
      const key = f.dbFeildName;
      if (key && row[key] !== undefined && row[key] !== null) {
        patch[key] = row[key];
      }
    });
    return patch;
  }

  private seedSelectOptions(formUIC: GenericFormUICDTO[], row: any): void {
    formUIC.forEach(f => {
      const type = (f.uiInputType ?? '').toLowerCase();
      if (!type.includes('select')) {
        return;
      }
      const value = row[f.dbFeildName];
      if (value === undefined || value === null || value === '') {
        return;
      }
      const valueKey = (f.patchPropertyName ?? '').trim() || 'key';
      const labelKey = (f.displayPropertyName ?? '').trim() || 'value';
      const existing = Array.isArray(f.data) ? f.data : [];
      const found = existing.some(o => String(o?.[valueKey] ?? '') === String(value));
      if (!found) {
        f.data = [...existing, { [valueKey]: value, [labelKey]: value }];
      }
    });
  }

  private decorate(): void {
    setTimeout(() => {
      this.decorateDateInputs();
      this.decorateRowDelete();
    });
  }

  private decorateRowDelete(): void {
    const host = this.hostRef.nativeElement as HTMLElement;
    const gutters = Array.from(host.querySelectorAll('.card > .d-flex.justify-content-end')) as HTMLElement[];

    gutters.forEach(gutter => {
      const own = gutter.querySelector('[data-own-delete="1"]');
      const engineButton = gutter.querySelector('button:not([data-own-delete])');

      if (engineButton) {
        if (own) {
          this.renderer.removeChild(gutter, own);
        }
        return;
      }

      if (own) {
        return;
      }

      const button = this.renderer.createElement('button');
      this.renderer.setAttribute(button, 'type', 'button');
      this.renderer.setAttribute(button, 'data-own-delete', '1');
      this.renderer.setAttribute(button, 'title', 'Remove');
      this.renderer.addClass(button, 'btn');
      this.renderer.addClass(button, 'btn-outline-danger');
      this.renderer.addClass(button, 'btn-sm');
      this.renderer.addClass(button, 'p-0');
      this.renderer.addClass(button, 'px-1');

      const icon = this.renderer.createElement('i');
      this.renderer.addClass(icon, 'fa');
      this.renderer.addClass(icon, 'fa-trash');
      this.renderer.appendChild(button, icon);

      this.renderer.listen(button, 'click', () => this.clearRows());
      this.renderer.appendChild(gutter, button);
    });
  }

  private decorateDateInputs(): void {
    const host = this.hostRef.nativeElement as HTMLElement;
    const inputs = Array.from(host.querySelectorAll('input[type="date"]')) as HTMLInputElement[];

    inputs.forEach(input => {
      if (input.dataset['clearable'] === '1') {
        return;
      }
      input.dataset['clearable'] = '1';
      const wrapper = input.parentElement;
      if (!wrapper) {
        return;
      }
      this.renderer.addClass(wrapper, 'date-clear-wrap');
      const clear = this.renderer.createElement('span');
      this.renderer.addClass(clear, 'date-clear');
      this.renderer.setAttribute(clear, 'title', 'Clear');
      this.renderer.appendChild(clear, this.renderer.createText('×'));
      this.renderer.listen(clear, 'click', () => this.clearDateInput(input));
      this.renderer.appendChild(wrapper, clear);
    });
  }

  private clearDateInput(input: HTMLInputElement): void {
    const match = (input.getAttribute('id') || '').match(/^field_(\d+)_(.+)$/);
    const control = match
      ? this.rowsForm?.formInstances?.[Number(match[1])]?.formGroup?.get(match[2])
      : null;

    if (control) {
      control.setValue('');
      control.markAsDirty();
      return;
    }

    input.value = '';
    input.dispatchEvent(new Event('input'));
  }

  private persistCalls(payload: ItemDetailDatalistCreateBulkItem[]): Observable<any>[] {
    const calls: Observable<any>[] = [];
    const created = payload.filter(r => !r.datalistId || r.datalistId <= 0);
    const existing = payload.filter(r => !!r.datalistId && r.datalistId > 0);
    const keptIds = new Set(existing.map(r => r.datalistId));
    const removed = this.savedRows.filter(r => r.datalistId > 0 && !keptIds.has(r.datalistId));

    if (created.length) {
      calls.push(this.iddService.ItemDetail_createBulk({
        itemList: created,
        tenantId: this.User.companyId,
        tenantBranchId: this.User.loggedInBranchId,
        actionUser: this.User.userId.toString()
      }));
    }

    existing.forEach(r => calls.push(this.iddService.ItemDetail_update({
      ...r,
      datalistId: r.datalistId,
      itemId: r.itemId,
      dTitle: r.dTitle ?? '',
      dCode: r.dCode ?? '',
      dDesc: r.dDesc ?? '',
      dType: this.dType || r.dType || '',
      dValue: r.dValue ?? '',
      isRequired: r.isRequired ?? 0,
      displaySeq: r.displaySeq ?? 0,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId,
      isActive: 1,
      actionUser: this.User.userId.toString()
    })));

    removed.forEach(r => calls.push(this.iddService.ItemDetail_delete({
      datalistId: r.datalistId,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId,
      actionUser: this.User.userId.toString()
    })));

    return calls.length ? calls : [of(null)];
  }
}
